/**
 * Login/Register Page Script - Type-Safe Version
 */
import { getLoginSuccessRedirect, publicPage, type PageAuthContext } from '../lib/page-runtime';
import type { LoginRequest, RegisterRequest } from '../types/api';

type AuthRecoveryTone = 'success' | 'error' | 'info';

interface AuthRecoveryMessage {
  readonly tone: AuthRecoveryTone;
  readonly text: string;
  readonly actionHref?: string;
  readonly actionLabel?: string;
}

function authMessageClasses(tone: AuthRecoveryTone): string {
  if (tone === 'success') return 'mt-4 text-sm rounded-lg border border-brand-800 bg-brand-950/20 text-brand-300 p-3';
  if (tone === 'info') return 'mt-4 text-sm rounded-lg border border-sky-800 bg-sky-950/20 text-sky-200 p-3';
  return 'mt-4 text-sm rounded-lg border border-red-800 bg-red-950/20 text-red-300 p-3';
}

function renderAuthMessage(target: HTMLElement | null, message: AuthRecoveryMessage): void {
  if (!target) return;
  target.className = authMessageClasses(message.tone);
  target.textContent = '';
  const text = document.createElement('span');
  text.textContent = message.text;
  target.appendChild(text);
  if (message.actionHref && message.actionLabel) {
    const link = document.createElement('a');
    link.href = window.LoginPageData.buildAuthActionHref(message.actionHref, window.location.search);
    link.textContent = message.actionLabel;
    link.className = 'ml-2 font-medium underline underline-offset-4';
    target.appendChild(link);
  }
}

function clearAuthMessage(target: HTMLElement | null): void {
  if (!target) return;
  target.textContent = '';
  target.className = 'hidden mt-4 text-sm rounded-lg border border-zinc-800 bg-surface-1 p-3';
}

function setSubmitButtonBusy(button: HTMLButtonElement, busyHtml: string): () => void {
  const originalHtml = button.innerHTML;
  const originalAriaBusy = button.getAttribute('aria-busy');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.innerHTML = busyHtml;
  return function restoreSubmitButton() {
    button.disabled = false;
    button.innerHTML = originalHtml;
    if (originalAriaBusy === null) {
      button.removeAttribute('aria-busy');
    } else {
      button.setAttribute('aria-busy', originalAriaBusy);
    }
  };
}

function initLoginPage(ctx: PageAuthContext) {
  'use strict';

  const pageData = window.__SKILLFAB_PAGE__;
  if (!pageData || (pageData.name !== 'login' && pageData.name !== 'recovery' && pageData.name !== 'reset-password')) return;

  if (pageData.name === 'recovery') {
    const signInLink = document.getElementById('recovery-sign-in-link') as HTMLAnchorElement | null;
    if (signInLink) {
      signInLink.href = window.LoginPageData.buildAuthReturnHref('/login', window.location.search);
    }
    const form = document.getElementById('recovery-form') as HTMLFormElement | null;
    const emailInput = document.getElementById('recovery-email') as HTMLInputElement | null;
    const submitButton = document.getElementById('recovery-submit') as HTMLButtonElement | null;
    const submitHelp = document.getElementById('recovery-submit-help') as HTMLDivElement | null;
    let resetRequestedForEmail = '';

    function syncPasswordResetRequestSubmitState(): void {
      if (!submitButton || !emailInput) return;
      const formValues = {
        email: emailInput.value,
        lastRequestedEmail: resetRequestedForEmail,
      };
      const formState = window.LoginPageData.buildPasswordResetRequestFormState(formValues);
      const helpText = window.LoginPageData.buildPasswordResetRequestHelpText(formValues);
      submitButton.disabled = formState.submitDisabled;
      submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
      if (submitHelp) {
        submitHelp.textContent = helpText;
        submitHelp.classList.toggle('hidden', helpText.length === 0);
      }
      if (!formState.submitDisabled) {
        submitButton.innerHTML = '<i class="fas fa-paper-plane mr-2"></i>Send Reset Email';
      } else if (resetRequestedForEmail && window.LoginPageData.normalizePasswordResetEmail(emailInput.value) === resetRequestedForEmail) {
        submitButton.innerHTML = window.LoginPageData.buildPasswordResetRequestFeedback().buttonHtml;
      } else {
        submitButton.innerHTML = '<i class="fas fa-paper-plane mr-2"></i>Send Reset Email';
      }
    }

    emailInput?.addEventListener('input', function() {
      clearAuthMessage(document.getElementById('recovery-msg'));
      syncPasswordResetRequestSubmitState();
    });
    syncPasswordResetRequestSubmitState();

    form?.addEventListener('submit', async function(e) {
      e.preventDefault();
      const btn = document.getElementById('recovery-submit') as HTMLButtonElement;
      const msg = document.getElementById('recovery-msg') as HTMLDivElement;
      const rawEmail = (document.getElementById('recovery-email') as HTMLInputElement).value;
      const email = window.LoginPageData.normalizePasswordResetEmail(rawEmail);
      let restoreButton = true;
      const restoreSubmitButton = setSubmitButtonBusy(btn, '<i class="fas fa-spinner fa-spin mr-2"></i>Sending...');
      try {
        const result = await window.LoginPageData.requestPasswordReset(email, window.location.search);
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage(
          'recovery',
          result.ok ? 'success' : 'error',
          result.ok ? null : result.error,
        ));
        if (result.ok) {
          const feedback = window.LoginPageData.buildPasswordResetRequestFeedback();
          btn.innerHTML = feedback.buttonHtml;
          btn.disabled = feedback.buttonDisabled;
          btn.setAttribute('aria-disabled', feedback.buttonDisabled ? 'true' : 'false');
          btn.removeAttribute('aria-busy');
          resetRequestedForEmail = email;
          restoreButton = false;
        }
      } catch {
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('recovery', 'error', 'Network error. Please try again.'));
      } finally {
        if (restoreButton) {
          restoreSubmitButton();
          syncPasswordResetRequestSubmitState();
        }
      }
    });
    return;
  }

  if (pageData.name === 'reset-password') {
    const requestNewLink = document.getElementById('reset-password-new-link') as HTMLAnchorElement | null;
    if (requestNewLink) {
      requestNewLink.href = window.LoginPageData.buildAuthReturnHref('/forgot-password', window.location.search);
    }
    function readPasswordResetValues(): { password: string; confirm: string } {
      return {
        password: (document.getElementById('reset-password-new') as HTMLInputElement | null)?.value || '',
        confirm: (document.getElementById('reset-password-confirm') as HTMLInputElement | null)?.value || '',
      };
    }
    function syncPasswordResetSubmitState(): void {
      const submitButton = document.getElementById('reset-password-submit') as HTMLButtonElement | null;
      const submitHelp = document.getElementById('reset-password-submit-help') as HTMLDivElement | null;
      if (!submitButton) return;
      const formValues = readPasswordResetValues();
      const formState = window.LoginPageData.buildPasswordResetFormState(formValues);
      const helpText = window.LoginPageData.buildPasswordResetFormHelpText(formValues);
      submitButton.disabled = formState.submitDisabled;
      submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
      if (submitHelp) {
        submitHelp.textContent = helpText;
        submitHelp.classList.toggle('hidden', helpText.length === 0);
      }
    }
    const form = document.getElementById('reset-password-form') as HTMLFormElement | null;
    ['reset-password-new', 'reset-password-confirm'].forEach(function(id) {
      document.getElementById(id)?.addEventListener('input', function() {
        clearAuthMessage(document.getElementById('reset-password-msg'));
        syncPasswordResetSubmitState();
      });
    });
    syncPasswordResetSubmitState();
    form?.addEventListener('submit', async function(e) {
      e.preventDefault();
      const btn = document.getElementById('reset-password-submit') as HTMLButtonElement;
      const msg = document.getElementById('reset-password-msg') as HTMLDivElement;
      const { password, confirm } = readPasswordResetValues();
      const token = typeof pageData.token === 'string' ? pageData.token : '';

      if (!token) {
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('reset-password', 'error', 'missing token'));
        return;
      }
      if (password !== confirm) {
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('reset-password', 'error', 'Passwords do not match.'));
        return;
      }

      let restoreButton = true;
      const restoreSubmitButton = setSubmitButtonBusy(btn, '<i class="fas fa-spinner fa-spin mr-2"></i>Updating...');
      try {
        const result = await window.LoginPageData.resetPassword({ token, password });
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage(
          'reset-password',
          result.ok ? 'success' : 'error',
          result.ok ? null : result.error,
          result.ok ? result.data : undefined,
        ));
        if (result.ok) {
          const feedback = window.LoginPageData.buildPasswordResetSubmitFeedback();
          btn.innerHTML = feedback.buttonHtml;
          btn.disabled = feedback.buttonDisabled;
          btn.removeAttribute('aria-busy');
          restoreButton = false;
          window.location.href = window.LoginPageData.buildPasswordResetSuccessLoginHref(window.location.search, result.data);
        }
      } catch {
        renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('reset-password', 'error', 'Network error. Please try again.'));
      } finally {
        if (restoreButton) {
          restoreSubmitButton();
          syncPasswordResetSubmitState();
        }
      }
    });
    return;
  }

  const isRegister = pageData.isRegister === true;
  const authSwitchLink = document.getElementById('auth-switch-link') as HTMLAnchorElement | null;
  if (authSwitchLink) {
    authSwitchLink.href = window.LoginPageData.buildAuthSwitchHref(
      isRegister ? '/login' : '/register',
      window.location.search,
    );
  }
  if (ctx.loggedIn) {
    window.location.href = getLoginSuccessRedirect(window.location.search);
    return;
  }
  if (!isRegister) {
    const forgotPasswordLink = document.getElementById('forgot-password-link') as HTMLAnchorElement | null;
    if (forgotPasswordLink) {
      forgotPasswordLink.href = window.LoginPageData.buildAuthReturnHref('/forgot-password', window.location.search);
    }
    const notice = window.LoginPageData.buildLoginNoticeFromSearch(window.location.search);
    if (notice) {
      renderAuthMessage(document.getElementById('auth-msg'), notice);
      const nextHref = window.LoginPageData.consumeOneTimeLoginNoticeUrl(window.location.href);
      if (nextHref !== window.location.href) {
        window.history.replaceState(null, '', nextHref);
      }
    }
  }

  function readAuthFormValues(): { isRegister: boolean; username: string; email?: string; password: string } {
    return {
      isRegister,
      username: (document.getElementById('auth-username') as HTMLInputElement | null)?.value || '',
      email: (document.getElementById('auth-email') as HTMLInputElement | null)?.value || '',
      password: (document.getElementById('auth-password') as HTMLInputElement | null)?.value || '',
    };
  }

  function syncAuthSubmitState(): void {
    const submitButton = document.getElementById('auth-submit') as HTMLButtonElement | null;
    const submitHelp = document.getElementById('auth-submit-help') as HTMLDivElement | null;
    if (!submitButton) return;
    const formValues = readAuthFormValues();
    const formState = window.LoginPageData.buildAuthFormState(formValues);
    const helpText = window.LoginPageData.buildAuthFormHelpText(formValues);
    submitButton.disabled = formState.submitDisabled;
    submitButton.setAttribute('aria-disabled', formState.submitDisabled ? 'true' : 'false');
    if (submitHelp) {
      submitHelp.textContent = helpText;
      submitHelp.classList.toggle('hidden', helpText.length === 0);
    }
  }

  async function handleAuth(e: Event) {
    e.preventDefault();
    const btn = document.getElementById('auth-submit') as HTMLButtonElement;
    const msg = document.getElementById('auth-msg');
    let restoreButton = true;
    const restoreSubmitButton = setSubmitButtonBusy(btn, '<i class="fas fa-spinner fa-spin mr-2"></i>Please wait...');

    const username = (document.getElementById('auth-username') as HTMLInputElement).value.trim();
    const password = (document.getElementById('auth-password') as HTMLInputElement).value;

    try {
      if (isRegister) {
        const email = (document.getElementById('auth-email') as HTMLInputElement).value.trim();
        const requestData: RegisterRequest = { username, email, password };
        const result = await window.LoginPageData.register(requestData, window.location.search);

        if (result.ok) {
          renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('register', 'success', null, result.data));
          const feedback = window.LoginPageData.buildAuthSubmitSuccessFeedback('register');
          btn.innerHTML = feedback.buttonHtml;
          btn.disabled = feedback.buttonDisabled;
          btn.removeAttribute('aria-busy');
          restoreButton = false;
          showToast(result.data.verification_email_sent
            ? 'Account created. Verification email sent.'
            : 'Account created. Verify from Settings.');
          const redirectHref = window.LoginPageData.buildPostRegisterRedirect(
            getLoginSuccessRedirect(window.location.search),
            result.data,
          );
          window.location.href = redirectHref;
        } else {
          renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('register', 'error', result.error));
          showToast(result.error || 'Registration failed', 'error');
        }
      } else {
        const requestData: LoginRequest = { username, password };
        const result = await window.LoginPageData.login(requestData);

        if (result.ok) {
          renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('login', 'success'));
          const feedback = window.LoginPageData.buildAuthSubmitSuccessFeedback('login');
          btn.innerHTML = feedback.buttonHtml;
          btn.disabled = feedback.buttonDisabled;
          btn.removeAttribute('aria-busy');
          restoreButton = false;
          showToast('Welcome back!');
          window.location.href = getLoginSuccessRedirect(window.location.search);
        } else {
          renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage('login', 'error', result.error));
          showToast(result.error || 'Login failed', 'error');
        }
      }
    } catch (err) {
      renderAuthMessage(msg, window.LoginPageData.buildAuthRecoveryMessage(isRegister ? 'register' : 'login', 'error', 'Network error. Please try again.'));
      showToast('Network error', 'error');
    } finally {
      if (restoreButton) {
        restoreSubmitButton();
        syncAuthSubmitState();
      }
    }
  }

  // type="module" scripts execute after DOM is fully parsed.
  ['auth-username', 'auth-email', 'auth-password'].forEach(function(id) {
    document.getElementById(id)?.addEventListener('input', function() {
      clearAuthMessage(document.getElementById('auth-msg'));
      syncAuthSubmitState();
    });
  });
  syncAuthSubmitState();
  const form = document.querySelector('form');
  if (form) {
    form.addEventListener('submit', handleAuth);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  publicPage({
    init: initLoginPage,
    onAuth: function(ctx) {
      const pageData = window.__SKILLFAB_PAGE__;
      if (!pageData || (pageData.name !== 'login' && pageData.name !== 'register')) return;
      if (ctx.loggedIn) {
        window.location.href = getLoginSuccessRedirect(window.location.search);
      }
    },
    deferUntilCoreReady: false,
  });
}
